import * as CL from '@design-system/component-library';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../../common/constants/dsClasses.js';
import { invalidEInvoiceAddressMsg, t } from '../../../common/i18n/index.js';
import { ElectronicAddress } from '../../../common/react-hook-form/fields/ElectronicAddress.js';
import { ElectronicOperator } from '../../../common/react-hook-form/fields/ElectronicOperator.js';
import { getDeliveryMethodDropDownOptions } from '../../../common/utils/billingAccountUtils.js';
import { ELECTRONIC_ADDRESS_REGEX } from '../../../common/utils/validationUtils.js';
import { BillingAccountDeliveryMethod } from '../../../generated/api/models.js';
import { GridColumn } from '../../BillingAccountDetails/components/GridColumn.js';
import { AdditionalBillReceiversEdit } from './AdditionalBillReceiversEdit.js';
import { BillReceiverEdit } from './BillReceiverEdit.js';
import { DeliveryMethodAndLanguageSelection } from './DeliveryMethodAndLanguageSelection.js';
import type { EOperatorItem } from '../../../common/utils/billingAccountUtils.js';
import type { BillChannel, BillingAccount, Contact, ContactHeader } from '../../../generated/api/models.js';

export interface BillingAccountDeliveryMethodFormProps {
  billingAccount: BillingAccount;
  billChannels: BillChannel[];
  contacts: Contact[] | ContactHeader[];
}

export const BillingAccountDeliveryMethodForm = ({
  billingAccount,
  billChannels,
  contacts,
}: BillingAccountDeliveryMethodFormProps) => {
  const deliveryMethodOptions = getDeliveryMethodDropDownOptions(billingAccount, billChannels);
  const { setValue, watch, trigger } = useFormContext();
  const selectedDeliveryMethod = watch('deliveryMethod');

  const setDeliveryType = (deliveryMethod: BillingAccountDeliveryMethod) => {
    setValue('deliveryMethod', deliveryMethod);
  };

  const handleOperatorChange = (selectedOperator?: EOperatorItem) => {
    if (selectedOperator?.active && selectedOperator.electronicAddress) {
      setValue('billElectronicAddress', selectedOperator.electronicAddress, { shouldValidate: true });
    } else {
      setValue('billElectronicAddress', '', { shouldValidate: true });
    }
  };

  const validateBillElectronicAddress = (value: string) => {
    if (!ELECTRONIC_ADDRESS_REGEX.test(value)) {
      return t.NMXU(invalidEInvoiceAddressMsg);
    }
    return undefined;
  };

  useEffect(() => {
    void trigger(['billElectronicAddress', 'billElectronicOperator', 'billReceiverId']);
  }, [trigger, selectedDeliveryMethod]);

  switch (selectedDeliveryMethod) {
    case BillingAccountDeliveryMethod.ELECTRONIC:
      return (
        <CL.Grid>
          <DeliveryMethodAndLanguageSelection
            deliveryMethodOptions={deliveryMethodOptions}
            setDeliveryType={setDeliveryType}
          />
          <CL.GridRow>
            <GridColumn className="of-billing-account-details form-column edit-field-large">
              <ElectronicOperator
                name="billElectronicOperator"
                billChannels={billChannels}
                onOperatorChange={handleOperatorChange}
              />
            </GridColumn>
            <GridColumn className={`of-billing-account-details form-column edit-field ${dsClass.MARGIN_BOTTOM_4}`}>
              <ElectronicAddress
                name="billElectronicAddress"
                required={false}
                maxLength={40}
                validate={validateBillElectronicAddress}
              />
            </GridColumn>
          </CL.GridRow>
        </CL.Grid>
      );

    case BillingAccountDeliveryMethod.EMAIL:
      return (
        <>
          <DeliveryMethodAndLanguageSelection
            deliveryMethodOptions={deliveryMethodOptions}
            setDeliveryType={setDeliveryType}
          />
          <div className="of-billing-account-details">
            <CL.Grid>
              <BillReceiverEdit
                contacts={contacts}
                receiverSameAsContact={
                  billingAccount.billReceiverId === undefined ||
                  billingAccount.billReceiverId === billingAccount.billingContactId
                }
              />
              <CL.GridRow>
                <AdditionalBillReceiversEdit />
              </CL.GridRow>
            </CL.Grid>
          </div>
        </>
      );

    case BillingAccountDeliveryMethod.PAPER:
      return (
        <DeliveryMethodAndLanguageSelection
          deliveryMethodOptions={deliveryMethodOptions}
          setDeliveryType={setDeliveryType}
        />
      );
    default:
      return null;
  }
};
